/**
 * Pure state transitions for MessageReactionRow's small view machine (per-
 * message thumb + note feedback — see
 * src/components/assistant/message-reaction-row.tsx). Same "no React" split
 * as lib/handoff-timeout-view.ts's nextTimeoutView — the transition table is
 * unit-tested without mounting the row.
 *
 * Design principle this encodes (from the source doc): "A thumb should cost
 * nothing — one tap, done." and "note-without-thumb is a designed state, not
 * an accident." Both fall out of this table rather than being special-cased
 * in the component:
 *
 *   - TOGGLE_UP/TOGGLE_DOWN when the SAME sentiment is already active is a
 *     RETRACTION (sentiment -> null, any open panel closes) — tapping an
 *     active thumb again undoes it.
 *   - TOGGLE_UP when not already up sets sentiment and opens NO panel — the
 *     one-tap-done case; there is nothing left for the user to do.
 *   - TOGGLE_DOWN when not already down sets sentiment AND opens the REASON
 *     panel — the sentiment itself is already "sent" (one tap, done); the
 *     panel is purely an optional enrichment (reasons/comment) layered on
 *     top, never a gate the down-vote waits on.
 *   - OPEN_NOTE opens the NOTE panel WITHOUT touching sentiment at all — it
 *     works identically whether sentiment is currently null, up, or down,
 *     which is exactly what "a thumb is optional" for a note means.
 *   - CLOSE_PANEL (Skip / Cancel / after a successful Send) only ever clears
 *     the panel, never the sentiment — closing the detail panel must not
 *     silently retract a thumb the user already gave.
 */

export const ReactionSentiment = {
  UP: 'up',
  DOWN: 'down',
} as const;
export type ReactionSentiment =
  (typeof ReactionSentiment)[keyof typeof ReactionSentiment];

export const ReactionPanel = {
  NONE: 'none',
  REASON: 'reason',
  NOTE: 'note',
} as const;
export type ReactionPanel = (typeof ReactionPanel)[keyof typeof ReactionPanel];

export const ReactionAction = {
  TOGGLE_UP: 'toggle_up',
  TOGGLE_DOWN: 'toggle_down',
  OPEN_NOTE: 'open_note',
  CLOSE_PANEL: 'close_panel',
} as const;
export type ReactionAction =
  (typeof ReactionAction)[keyof typeof ReactionAction];

export interface ReactionState {
  sentiment: ReactionSentiment | null;
  panel: ReactionPanel;
}

export const INITIAL_REACTION_STATE: ReactionState = {
  sentiment: null,
  panel: ReactionPanel.NONE,
};

/** Returns the state MessageReactionRow should move to after `action` fires,
 *  given the CURRENT state — unlike nextTimeoutView, this table genuinely
 *  needs `current` (retraction depends on whether the tapped thumb is
 *  already the active sentiment). */
export function nextReactionState(
  current: ReactionState,
  action: ReactionAction
): ReactionState {
  switch (action) {
    case ReactionAction.TOGGLE_UP:
      return current.sentiment === ReactionSentiment.UP
        ? { sentiment: null, panel: ReactionPanel.NONE }
        : { sentiment: ReactionSentiment.UP, panel: ReactionPanel.NONE };
    case ReactionAction.TOGGLE_DOWN:
      return current.sentiment === ReactionSentiment.DOWN
        ? { sentiment: null, panel: ReactionPanel.NONE }
        : { sentiment: ReactionSentiment.DOWN, panel: ReactionPanel.REASON };
    case ReactionAction.OPEN_NOTE:
      return { sentiment: current.sentiment, panel: ReactionPanel.NOTE };
    case ReactionAction.CLOSE_PANEL:
      return { sentiment: current.sentiment, panel: ReactionPanel.NONE };
    default:
      // Unreachable — exhaustive over ReactionAction (see nextTimeoutView's
      // identical fallback for why this repo's tsconfig doesn't need a
      // `never` cast here).
      return current;
  }
}
